import Big from 'big.js';
import { AmountInputConfig } from '../config/number-precision.config';

/**
 * Normalizes Persian and Arabic digits to English digits
 */
export function normalizeDigits(value: string): string {
  const persian = '۰۱۲۳۴۵۶۷۸۹';
  const arabic = '٠١٢٣٤٥٦٧٨٩';
  const english = '0123456789';

  let result = '';
  for (const char of value) {
    const pi = persian.indexOf(char);
    if (pi !== -1) {
      result += english[pi];
      continue;
    }
    const ai = arabic.indexOf(char);
    if (ai !== -1) {
      result += english[ai];
      continue;
    }
    result += char;
  }
  return result;
}

/**
 * Removes thousand separators from a formatted number string
 */
export function removeSeparators(value: string, separator: string): string {
  return value.split(separator).join('');
}

/**
 * Adds thousand separators to a number string
 */
export function addThousandSeparators(
  value: string,
  separator: string,
): string {
  const parts = value.split('.');
  const integerPart = parts[0] ?? '';
  const decimalPart = parts[1];

  const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return decimalPart !== undefined ? `${formatted}.${decimalPart}` : formatted;
}

/**
 * Validates if a string represents a valid number
 */
export function isValidNumberString(value: string): boolean {
  if (!value || value === '-') return true;

  try {
    new Big(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Formats a number string with thousand separators and precision
 */
export function formatNumberDisplay(
  value: string | number | null | undefined,
  config: AmountInputConfig,
): string {
  if (value == null || value === '') return '';

  const normalized = normalizeDigits(String(value));
  const clean = removeSeparators(normalized, config.thousandSeparator);

  if (!isValidNumberString(clean)) return '';

  try {
    const big = new Big(clean);
    const fixed = big.toFixed(config.maxDecimalDigits);

    const trimmed = fixed.replace(/\.0+$|(?<=\.[0-9]*[1-9])0+$/u, '');

    return addThousandSeparators(trimmed, config.thousandSeparator);
  } catch {
    return '';
  }
}

/**
 * Parses a formatted number string to a clean string value
 */
export function parseNumberInput(
  value: string,
  config: AmountInputConfig,
): string {
  if (!value) return '';

  const normalized = normalizeDigits(value);
  const clean = removeSeparators(normalized, config.thousandSeparator);

  if (!isValidNumberString(clean)) return '';

  return clean;
}

/**
 * Validates and formats input while preserving partial input states
 */
export function formatPartialInput(
  value: string,
  config: AmountInputConfig,
): string {
  if (!value) return '';

  const normalized = normalizeDigits(value);
  let clean = removeSeparators(normalized, config.thousandSeparator);

  const parts = clean.split('.');
  const integerPart = parts[0] ?? '';
  const decimalPart = parts[1];

  if (integerPart.length > config.maxIntegerDigits) {
    clean = integerPart.slice(0, config.maxIntegerDigits);
    if (decimalPart !== undefined) {
      clean += '.' + decimalPart;
    }
  }

  if (
    decimalPart !== undefined &&
    decimalPart.length > config.maxDecimalDigits
  ) {
    clean = integerPart + '.' + decimalPart.slice(0, config.maxDecimalDigits);
  }

  const formatted = addThousandSeparators(
    parts[0] ?? '',
    config.thousandSeparator,
  );

  if (decimalPart !== undefined) {
    return `${formatted}.${decimalPart}`;
  }

  if (clean.endsWith('.')) {
    return `${formatted}.`;
  }

  return formatted;
}

export function safeMultiply(a: string, b: string): string | null {
  const result = parseFloat(a) * parseFloat(b);
  return isNaN(result) ? null : result.toString();
}
